import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle2, XCircle, Clock, Loader2, ArrowRight, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { paymentsAPI } from '@/utils/api';
import { formatCOP, CONTACT } from '@/utils/format';

const POLL_INTERVAL_MS = 4000;
const MAX_POLLS = 15; // ~1 minute of polling for delayed webhook

const STATE_META = {
  approved: {
    icon: CheckCircle2,
    color: 'text-[#2A5C43]',
    bg: 'bg-[#F7F2E8]',
    title: '¡Pago aprobado!',
    message: 'Tu pedido está confirmado. En breve recibirás un correo con el detalle.',
  },
  paid: {
    icon: CheckCircle2,
    color: 'text-[#2A5C43]',
    bg: 'bg-[#F7F2E8]',
    title: '¡Pago aprobado!',
    message: 'Tu pedido está confirmado. En breve recibirás un correo con el detalle.',
  },
  rejected: {
    icon: XCircle,
    color: 'text-red-600',
    bg: 'bg-red-50',
    title: 'Pago rechazado',
    message: 'Bold rechazó el pago. Intenta con otro método o comunícate con nosotros por WhatsApp.',
  },
  failed: {
    icon: XCircle,
    color: 'text-red-600',
    bg: 'bg-red-50',
    title: 'Pago fallido',
    message: 'Algo salió mal durante el proceso de pago. No se realizó ningún cobro.',
  },
  processing: {
    icon: Clock,
    color: 'text-[#D47A4A]',
    bg: 'bg-orange-50',
    title: 'Pago en proceso',
    message: 'Bold está confirmando tu pago. Actualiza en unos minutos o revisa tus pedidos.',
  },
  intent_created: {
    icon: Clock,
    color: 'text-[#D47A4A]',
    bg: 'bg-orange-50',
    title: 'Esperando confirmación',
    message: 'Estamos esperando que Bold confirme el resultado.',
  },
};

const PaymentResult = () => {
  const [params] = useSearchParams();
  const reference = params.get('ref') || params.get('reference');
  const [payment, setPayment] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [polls, setPolls] = useState(0);

  useEffect(() => {
    if (!reference) {
      setError('No se recibió la referencia del pago.');
      setLoading(false);
      return;
    }

    let cancelled = false;
    let timer;

    const fetchOnce = async () => {
      try {
        const data = await paymentsAPI.getStatus(reference);
        if (cancelled) return;
        setPayment(data);
        setLoading(false);

        const s = (data.status || '').toLowerCase();
        const remote = (data.last_bold_status || '').toLowerCase();
        const done = ['approved', 'paid', 'rejected', 'failed'].includes(s) ||
                     ['paid', 'rejected', 'cancelled', 'expired'].includes(remote);

        if (!done && polls < MAX_POLLS) {
          setPolls((p) => p + 1);
          timer = setTimeout(fetchOnce, POLL_INTERVAL_MS);
        }
      } catch (e) {
        if (cancelled) return;
        setError(e.response?.data?.detail || 'No pudimos consultar el estado del pago.');
        setLoading(false);
      }
    };

    fetchOnce();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reference]);

  // Normalise the display status
  const displayKey = (() => {
    if (!payment) return 'intent_created';
    const s = (payment.status || '').toLowerCase();
    if (STATE_META[s]) return s;
    const rem = (payment.last_bold_status || '').toLowerCase();
    if (rem === 'paid') return 'approved';
    if (['rejected', 'cancelled', 'expired'].includes(rem)) return 'rejected';
    if (rem === 'processing') return 'processing';
    return 'intent_created';
  })();

  const meta = STATE_META[displayKey] || STATE_META.intent_created;
  const Icon = meta.icon;

  const whatsappUrl = `https://wa.me/${CONTACT.whatsappDigits}?text=${encodeURIComponent(
    `Hola, tuve un problema con el pago. Referencia: ${reference}`
  )}`;

  return (
    <div className="max-w-3xl mx-auto px-4 lg:px-8 py-16">
      <div className="bg-white rounded-3xl border border-[#E7E5E4] overflow-hidden" data-testid="payment-result-card">
        <div className={`${meta.bg} p-10 text-center`}>
          {loading ? (
            <>
              <Loader2 className="w-14 h-14 animate-spin text-[#2A5C43] mx-auto mb-4" />
              <h1 className="font-display font-bold text-2xl text-[#1C1917]">Consultando el pago…</h1>
              <p className="text-sm text-[#57534E] mt-2">Un momento, por favor.</p>
            </>
          ) : (
            <>
              <div className={`w-16 h-16 rounded-full ${meta.bg} border-4 border-white shadow-sm mx-auto flex items-center justify-center mb-4`}>
                <Icon className={`w-9 h-9 ${meta.color}`} />
              </div>
              <p className="text-xs tracking-[0.25em] uppercase text-[#8BA58F] font-semibold mb-1">
                Estado del pago
              </p>
              <h1
                className="font-display font-bold text-3xl sm:text-4xl text-[#1C1917] tracking-tight"
                data-testid="payment-result-title"
              >
                {meta.title}
              </h1>
              <p className="text-[#57534E] mt-3 max-w-md mx-auto">{meta.message}</p>
              {polls > 0 && displayKey === 'intent_created' && (
                <p className="text-xs text-[#8BA58F] mt-3">
                  Bold puede tardar unos segundos en confirmar. Consulta {polls}/{MAX_POLLS}
                </p>
              )}
            </>
          )}
        </div>

        {payment && !loading && (
          <div className="p-6 sm:p-8 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-xs uppercase tracking-widest text-[#8BA58F] font-semibold mb-0.5">Referencia</p>
                <p className="font-medium text-[#1C1917] break-all" data-testid="payment-result-reference">{payment.reference}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-widest text-[#8BA58F] font-semibold mb-0.5">Monto</p>
                <p className="font-display font-bold text-[#1C1917]">{formatCOP(payment.amount)}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-widest text-[#8BA58F] font-semibold mb-0.5">Estado interno</p>
                <p className="text-[#1C1917]">{payment.status}</p>
              </div>
              {payment.last_bold_status && (
                <div>
                  <p className="text-xs uppercase tracking-widest text-[#8BA58F] font-semibold mb-0.5">Estado en Bold</p>
                  <p className="text-[#1C1917]">{payment.last_bold_status}</p>
                </div>
              )}
            </div>

            <div className="border-t border-[#F5F5F4] pt-4 flex flex-wrap gap-2">
              <Button asChild className="bg-[#2A5C43] hover:bg-[#214A35] text-white rounded-full">
                <Link to="/mis-pedidos" data-testid="payment-result-orders">
                  Ver mis pedidos <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
              <Button asChild variant="outline" className="rounded-full border-[#2A5C43] text-[#2A5C43]">
                <Link to="/perros" data-testid="payment-result-continue">Seguir comprando</Link>
              </Button>
              {displayKey !== 'approved' && (
                <Button asChild variant="outline" className="rounded-full border-[#25D366] text-[#25D366]">
                  <a href={whatsappUrl} target="_blank" rel="noreferrer" data-testid="payment-result-whatsapp">
                    <MessageCircle className="w-4 h-4 mr-2" /> Ayuda por WhatsApp
                  </a>
                </Button>
              )}
            </div>
          </div>
        )}

        {error && !payment && (
          <div className="p-6 sm:p-8 text-center">
            <p className="text-red-700" data-testid="payment-result-error">{error}</p>
            <Button asChild className="mt-4 bg-[#2A5C43] hover:bg-[#214A35] text-white rounded-full">
              <Link to="/mis-pedidos">Ver mis pedidos</Link>
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentResult;
